import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Activity, ChevronRight, AlertCircle, FileText } from 'lucide-react';
import { toast } from 'sonner';
import ResidualLimbLogModal from './ResidualLimbLogModal';
import CareTeamExportModal from './CareTeamExportModal';
import { useFeatureFlag } from '../context/FeatureFlagsContext';
import { skinStatusTone, todayLocalISO, lastNDays } from '../data/residualLimbLog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResidualLimbLogCard = () => {
  const [open, setOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [logs, setLogs] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const { enabled: careTeamPdfEnabled } = useFeatureFlag('care_team_pdf');

  const today = todayLocalISO();
  const days = lastNDays(7); // newest first

  const refresh = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/residual-limb-logs`);
      setLogs(res.data?.logs || []);
    } catch {
      setLogs([]);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Daily reminder — fire once per local day if today's log is missing.
  // Uses localStorage so it persists across sessions/tabs but resets each
  // new day. When push-notification infra ships (backlog #10), wire this
  // into APNs/FCM as well.
  useEffect(() => {
    if (!loaded) return;
    const todayLogged = logs.find((l) => l.date === today);
    if (todayLogged) return;
    const stampKey = 'residualLimbReminder.lastFired';
    const lastFired = localStorage.getItem(stampKey);
    if (lastFired === today) return;
    localStorage.setItem(stampKey, today);
    toast('Residual-limb check-in pending', {
      description: "Tap to log today's skin, socket, phantom-limb pain, and wear time.",
      duration: 10000,
      action: {
        label: 'Log now',
        onClick: () => setOpen(true),
      },
    });
  }, [loaded, logs, today]);

  const logsByDate = Object.fromEntries(logs.map((l) => [l.date, l]));
  const todayLog = logsByDate[today];
  const status = todayLog?.skin?.status;

  return (
    <>
      <div className="bg-enf-surface-deep border border-enf-surface-border rounded-xl p-6" data-testid="residual-limb-card">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-adaptive-accent" />
            <h3 className="text-sm font-semibold uppercase tracking-widest text-enf-text-secondary">Residual-limb log</h3>
          </div>
          {!todayLog && (
            <span className="flex items-center gap-1 text-[11px] text-adaptive-accent animate-pulse">
              <AlertCircle className="w-3 h-3" /> Today's check-in pending
            </span>
          )}
        </div>

        {/* 7-day strip */}
        <div className="flex items-center gap-1 mb-4" data-testid="residual-limb-7day-strip">
          {[...days].reverse().map((d) => {
            const log = logsByDate[d];
            const tone = log ? skinStatusTone(log.skin?.status) : 'bg-enf-surface-mid';
            const isToday = d === today;
            return (
              <div
                key={d}
                title={`${d}${log?.skin?.status ? ' — ' + log.skin.status : ''}`}
                className={`flex-1 h-6 rounded ${tone} ${isToday ? 'ring-2 ring-adaptive-accent/60' : ''}`}
              />
            );
          })}
        </div>

        <div className="flex items-center justify-between">
          <div className="text-xs text-enf-text-muted">
            {todayLog
              ? `Today: ${status ? status.replace('-', ' ') : 'logged'} · ${todayLog.wear_time_hours ?? 0}h wear`
              : 'No entry yet today'}
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setExportOpen(true)}
              data-testid="care-team-export-button"
              className="text-xs font-semibold text-enf-text-secondary hover:text-adaptive-accent flex items-center gap-1"
              style={{ display: careTeamPdfEnabled ? undefined : 'none' }}
            >
              <FileText className="w-3 h-3" /> Care summary
            </button>
            <button
              type="button"
              onClick={() => setOpen(true)}
              data-testid="residual-limb-log-button"
              className="text-xs font-semibold text-adaptive-accent hover:text-adaptive-accent-hover flex items-center gap-1"
            >
              {todayLog ? 'Edit today' : 'Log today'} <ChevronRight className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>

      <ResidualLimbLogModal
        open={open}
        onOpenChange={setOpen}
        date={today}
        onSaved={() => refresh()}
      />
      <CareTeamExportModal open={exportOpen} onOpenChange={setExportOpen} />
    </>
  );
};

export default ResidualLimbLogCard;
